import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { API, formatCurrency } from '../lib/utils';
import { 
  UNIT_OPTIONS, 
  getUnitLabel, 
  getPrestationLabel, 
  getDomainLabel,
  getCategoryGroup, 
  TVA_RATES 
} from '../config/serviceCategories';
import { toast } from 'sonner';
import { 
  Plus, Trash2, ChevronLeft, Save, FileText, 
  User, Package, Percent, Euro, Search, Check, AlertTriangle, Info
} from 'lucide-react';

const InvoiceBuilderPage = () => {
  const navigate = useNavigate();
  const { id: invoiceId } = useParams();
  const { company } = useAuth();
  
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [originalInvoice, setOriginalInvoice] = useState(null);
  
  // Invoice data
  const [selectedClientId, setSelectedClientId] = useState('');
  const [lines, setLines] = useState([]);
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Paiement à 30 jours');
  const [paymentDueDays, setPaymentDueDays] = useState(30);
  // New fields
  const [acomptePercent, setAcomptePercent] = useState('');
  const [clientOrderNumber, setClientOrderNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  // New line form
  const [newLine, setNewLine] = useState({
    description: '',
    quantity: '1',
    unit: 'hour',
    unit_price_ht: '',
    tva_rate: 20,
    service_id: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Pre-fill payment terms from company profile if available and not editing
  useEffect(() => {
    if (!invoiceId && company?.default_payment_delay && paymentTerms === 'Paiement à 30 jours') {
      const delayMapping = {
        'reception': 'Paiement à réception de facture',
        '30': 'Paiement sous 30 jours',
        '45': 'Paiement sous 45 jours',
        '60': 'Paiement sous 60 jours'
      };
      const mappedTerms = delayMapping[company.default_payment_delay];
      if (mappedTerms) {
        setPaymentTerms(mappedTerms);
      }
    }
  }, [company, invoiceId, paymentTerms]);

  const fetchData = async () => {
    try {
      const [clientsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/services`)
      ]);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      
      // Load existing invoice for edit mode
      if (invoiceId) {
        const invoiceRes = await axios.get(`${API}/invoices/${invoiceId}`);
        const invoice = invoiceRes.data;
        
        // Check if editable
        if (invoice.status !== 'brouillon') {
          toast.error('Seules les factures en brouillon peuvent être modifiées');
          navigate(`/invoices/${invoiceId}`);
          return;
        }
        
        setOriginalInvoice(invoice);
        setSelectedClientId(invoice.client_id);
        setLines(invoice.lines.map(ln => ({
          id: ln.id || Date.now() + Math.random(),
          description: ln.description,
          quantity: ln.quantity,
          unit: ln.unit,
          unit_price_ht: ln.unit_price_ht,
          tva_rate: ln.tva_rate,
          service_id: ln.service_id
        })));
        setDiscountType(invoice.discount_type || '');
        setDiscountValue(invoice.discount_value?.toString() || '');
        setNotes(invoice.notes || '');
        setPaymentTerms(invoice.payment_terms || 'Paiement à 30 jours');
        setAcomptePercent(invoice.acompte_percent?.toString() || '');
        setClientOrderNumber(invoice.client_order_number || '');
        setPaymentMethod(invoice.payment_method || '');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      navigate('/invoices');
    }
    setLoading(false);
  };

  // Check if company uses TVA franchise
  const isFranchise = company?.tva_regime === 'franchise';

  const addLineFromService = (service) => {
    const prestationLabel = getPrestationLabel(service.category, service.domain) || service.description;
    const line = {
      id: Date.now(),
      description: prestationLabel,
      quantity: 1,
      unit: service.unit,
      unit_price_ht: service.unit_price_ht,
      tva_rate: isFranchise ? 0 : service.tva_rate,
      service_id: service.id
    };
    setLines([...lines, line]);
    setServiceDialogOpen(false);
    setCatalogSearch('');
    toast.success(`✓ ${prestationLabel} ajouté`);
  };

  const filteredServices = useMemo(() => {
    if (!catalogSearch.trim()) return services;
    const search = catalogSearch.toLowerCase();
    return services.filter(s => {
      const prestationLabel = getPrestationLabel(s.category, s.domain) || s.description;
      const domainLabel = getDomainLabel(s.domain) || '';
      return prestationLabel.toLowerCase().includes(search) ||
             domainLabel.toLowerCase().includes(search);
    });
  }, [services, catalogSearch]);

  const groupedCatalogServices = useMemo(() => {
    const groups = {};
    filteredServices.forEach(service => {
      const groupName = service.domain 
        ? getDomainLabel(service.domain).toUpperCase()
        : getCategoryGroup(service.category);
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(service);
    });
    return groups;
  }, [filteredServices]);

  const addCustomLine = () => {
    if (!newLine.description || !newLine.unit_price_ht) {
      toast.error('Veuillez remplir la description et le prix');
      return;
    }
    const line = {
      id: Date.now(),
      description: newLine.description,
      quantity: parseFloat(newLine.quantity) || 1,
      unit: newLine.unit,
      unit_price_ht: parseFloat(newLine.unit_price_ht),
      tva_rate: isFranchise ? 0 : newLine.tva_rate,
      service_id: null
    };
    setLines([...lines, line]);
    setNewLine({
      description: '',
      quantity: '1',
      unit: 'hour',
      unit_price_ht: '',
      tva_rate: isFranchise ? 0 : 20,
      service_id: null
    });
    toast.success('Ligne ajoutée');
  };

  const updateLine = (lineId, field, value) => {
    setLines(lines.map(line => 
      line.id === lineId ? { ...line, [field]: value } : line
    ));
  };

  const removeLine = (lineId) => {
    setLines(lines.filter(line => line.id !== lineId));
  };

  // Calculate totals
  const subtotalHT = lines.reduce((sum, line) => sum + (line.quantity * line.unit_price_ht), 0);
  const subtotalTVA = lines.reduce((sum, line) => {
    return sum + (line.quantity * line.unit_price_ht * (line.tva_rate / 100));
  }, 0);

  let discount = 0;
  if (discountType === 'percent' && discountValue) {
    discount = subtotalHT * (parseFloat(discountValue) / 100);
  } else if (discountType === 'fixed' && discountValue) {
    discount = parseFloat(discountValue);
  }

  const totalHT = subtotalHT - discount;
  const totalTVA = subtotalHT > 0 ? subtotalTVA * (1 - discount / subtotalHT) : 0;
  const totalTTC = totalHT + totalTVA;
  
  const acompteAmount = acomptePercent ? totalTTC * (parseFloat(acomptePercent) / 100) : 0;

  const handleSave = async () => {
    if (!selectedClientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (lines.length === 0) {
      toast.error('Ajoutez au moins une ligne à la facture');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client_id: selectedClientId,
        lines: lines.map(line => ({
          description: line.description,
          quantity: parseFloat(line.quantity),
          unit: line.unit,
          unit_price_ht: parseFloat(line.unit_price_ht),
          tva_rate: parseFloat(line.tva_rate),
          service_id: line.service_id
        })),
        discount_type: discountType || null,
        discount_value: discountValue ? parseFloat(discountValue) : 0,
        notes: notes || null,
        payment_terms: paymentTerms,
        payment_due_days: paymentDueDays,
        acompte_percent: acomptePercent ? parseFloat(acomptePercent) : null,
        client_order_number: clientOrderNumber || null,
        payment_method: paymentMethod || null
      };

      await axios.put(`${API}/invoices/${invoiceId}`, payload);
      toast.success('Facture modifiée avec succès !');
      navigate(`/invoices/${invoiceId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-48 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-48 md:pb-8 p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">
              Modifier {originalInvoice?.invoice_number || 'facture'}
            </h1>
            {originalInvoice?.status === 'brouillon' && (
              <p className="text-sm text-muted-foreground mt-1">
                <AlertTriangle className="w-4 h-4 inline mr-1 text-blue-400" />
                Cette facture est en brouillon et peut être modifiée
              </p>
            )}
          </div>
        </div>

        {/* TVA Franchise Banner */}
        {isFranchise && (
          <div className="flex items-center gap-3 p-4 mb-6 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-400">
              Franchise en base de TVA - TVA non applicable. Tous les montants sont hors taxe.
            </p>
          </div>
        )}

        {/* Step 1: Select Client */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-primary" />
              1. Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="client-select">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10 max-h-60">
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} {client.company && `(${client.company})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Step 2: Lines */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-primary" />
                2. Prestations
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setServiceDialogOpen(true)}
                className="border-primary text-primary hover:bg-primary/10"
                data-testid="add-from-catalog-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Catalogue
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Lines List */}
            {lines.length > 0 ? (
              <div className="space-y-3 mb-4">
                {lines.map((line, index) => (
                  <div 
                    key={line.id} 
                    className="bg-muted/30 rounded-lg p-3"
                    data-testid={`line-${index}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Input
                        value={line.description}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        className="flex-1 h-10 bg-transparent border-transparent hover:bg-muted/50 focus:bg-muted/50"
                        data-testid={`line-${index}-description`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(line.id)}
                        className="h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/20"
                        data-testid={`line-${index}-delete`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className={`grid ${line.unit === 'forfait' ? (isFranchise ? 'grid-cols-2' : 'grid-cols-3') : (isFranchise ? 'grid-cols-3' : 'grid-cols-4')} gap-2`}>
                      {line.unit !== 'forfait' && (
                        <Input
                          type="number"
                          value={line.quantity}
                          onChange={(e) => updateLine(line.id, 'quantity', parseFloat(e.target.value) || 0)}
                          className="h-9 bg-muted/50 border-transparent text-center"
                          data-testid={`line-${index}-qty`}
                        />
                      )}
                      <Select value={line.unit} onValueChange={(v) => {
                        updateLine(line.id, 'unit', v);
                        if (v === 'forfait') updateLine(line.id, 'quantity', 1);
                      }}>
                        <SelectTrigger className="h-9 bg-muted/50 border-transparent">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10">
                          {UNIT_OPTIONS.filter(u => !u.isCustom).map(u => (
                            <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        value={line.unit_price_ht}
                        onChange={(e) => updateLine(line.id, 'unit_price_ht', parseFloat(e.target.value) || 0)}
                        className="h-9 bg-muted/50 border-transparent"
                        placeholder={line.unit === 'forfait' ? 'Montant HT' : ''}
                        data-testid={`line-${index}-price`}
                      />
                      {!isFranchise && (
                        <Select value={line.tva_rate.toString()} onValueChange={(v) => updateLine(line.id, 'tva_rate', parseFloat(v))}>
                          <SelectTrigger className="h-9 bg-muted/50 border-transparent">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-white/10">
                            {TVA_RATES.map(rate => (
                              <SelectItem key={rate.value} value={rate.value.toString()}>{rate.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="flex justify-end mt-2 text-sm">
                      <span className="text-primary font-semibold">
                        {formatCurrency(isFranchise ? line.quantity * line.unit_price_ht : line.quantity * line.unit_price_ht * (1 + line.tva_rate / 100))} {isFranchise ? '' : 'TTC'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Ajoutez des prestations depuis le catalogue ou manuellement</p>
              </div>
            )}

            {/* Add Custom Line */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <p className="text-sm text-muted-foreground mb-3">Ou ajouter manuellement :</p>
              <div className={`grid grid-cols-2 ${newLine.unit === 'forfait' ? 'md:grid-cols-4' : 'md:grid-cols-5'} gap-2 mb-2`}>
                <Input
                  placeholder="Description"
                  value={newLine.description}
                  onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                  className="col-span-2 md:col-span-1 h-12 bg-muted/50 border-transparent"
                  data-testid="new-line-description"
                />
                {newLine.unit !== 'forfait' && (
                  <Input
                    type="number"
                    placeholder="Qté"
                    value={newLine.quantity}
                    onChange={(e) => setNewLine({ ...newLine, quantity: e.target.value })}
                    className="h-12 bg-muted/50 border-transparent"
                    data-testid="new-line-qty"
                  />
                )}
                <Select value={newLine.unit} onValueChange={(v) => setNewLine({ ...newLine, unit: v, quantity: v === 'forfait' ? '1' : newLine.quantity })}>
                  <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="new-line-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {UNIT_OPTIONS.filter(u => !u.isCustom).map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder={newLine.unit === 'forfait' ? 'Montant forfaitaire HT' : 'Prix HT'}
                  value={newLine.unit_price_ht}
                  onChange={(e) => setNewLine({ ...newLine, unit_price_ht: e.target.value })}
                  className="h-12 bg-muted/50 border-transparent"
                  data-testid="new-line-price"
                />
                {!isFranchise && (
                  <Select value={newLine.tva_rate.toString()} onValueChange={(v) => setNewLine({ ...newLine, tva_rate: parseFloat(v) })}>
                    <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="new-line-tva">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {TVA_RATES.map(rate => (
                        <SelectItem key={rate.value} value={rate.value.toString()}>TVA {rate.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addCustomLine}
                className="w-full h-12 border-white/10"
                data-testid="add-custom-line-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter cette ligne
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Catalog Dialog */}
        <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
          <DialogContent 
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className="bg-card border-white/10 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Ajouter depuis le catalogue</DialogTitle>
            </DialogHeader>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                className="pl-10 h-12 bg-muted/50 border-transparent"
                data-testid="catalog-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto space-y-4">
              {Object.entries(groupedCatalogServices).map(([group, groupServices]) => (
                <div key={group}>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{group}</p>
                  <div className="space-y-2">
                    {groupServices.map(service => {
                      const prestationLabel = getPrestationLabel(service.category, service.domain) || service.description;
                      return (
                        <div
                          key={service.id}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer"
                          onClick={() => addLineFromService(service)}
                          data-testid={`catalog-service-${service.id}`}
                        >
                          <div>
                            <p className="font-medium">{prestationLabel}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatCurrency(service.unit_price_ht)} / {getUnitLabel(service.unit)}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {Object.keys(groupedCatalogServices).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun service trouvé
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Step 3: Options */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-primary" />
              3. Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Discount */}
            <div>
              <Label className="text-sm text-muted-foreground">Remise</Label>
              <div className="flex gap-2 mt-1">
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger className="w-32 h-12 bg-muted/50 border-transparent" data-testid="discount-type-select">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="none">Aucune</SelectItem>
                    <SelectItem value="percent">%</SelectItem>
                    <SelectItem value="fixed">€</SelectItem>
                  </SelectContent>
                </Select>
                {discountType && discountType !== 'none' && (
                  <Input
                    type="number"
                    placeholder={discountType === 'percent' ? '10' : '50'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="flex-1 h-12 bg-muted/50 border-transparent"
                    data-testid="discount-value-input"
                  />
                )}
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <Label>Mode de paiement</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="mt-1 h-12 bg-muted/50 border-transparent" data-testid="payment-method-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="">Non spécifié</SelectItem>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="carte">Carte bancaire</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Acompte */}
            <div>
              <Label htmlFor="acompte">Acompte (%)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="acompte"
                  type="number"
                  value={acomptePercent}
                  onChange={(e) => setAcomptePercent(e.target.value)}
                  className="h-12 bg-muted/50 border-transparent focus:border-primary w-24"
                  placeholder="30"
                  data-testid="acompte-input"
                />
                <span className="text-muted-foreground">%</span>
                {acompteAmount > 0 && (
                  <span className="text-primary font-semibold">
                    soit {formatCurrency(acompteAmount)} TTC
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Acompte à verser à la commande
              </p>
            </div>

            {/* Client Order Number */}
            <div>
              <Label htmlFor="client_order">N° de commande client (optionnel)</Label>
              <Input
                id="client_order"
                value={clientOrderNumber}
                onChange={(e) => setClientOrderNumber(e.target.value)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary"
                placeholder="PO-12345"
                data-testid="client-order-number"
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 bg-muted/50 border-transparent focus:border-primary min-h-[80px]"
                placeholder="Notes ou conditions particulières..."
                data-testid="invoice-notes"
              />
            </div>

            {/* Payment Terms */}
            <div>
              <Label htmlFor="payment_terms">Conditions de paiement</Label>
              <Input
                id="payment_terms"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary"
                data-testid="payment-terms"
              />
            </div>

            {/* Payment Due Days */}
            <div>
              <Label htmlFor="due_days">Échéance (jours)</Label>
              <Input
                id="due_days"
                type="number"
                value={paymentDueDays}
                onChange={(e) => setPaymentDueDays(parseInt(e.target.value) || 30)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary w-32"
                data-testid="due-days"
              />
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <div className="quote-totals-sticky md:mb-6">
          <Card className="bg-card border-white/10">
            <CardContent className="p-4 md:p-6">
              <div className="space-y-2">
                {isFranchise ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Sous-total</span>
                      <span>{formatCurrency(subtotalHT)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-destructive">
                        <span>Remise</span>
                        <span>-{formatCurrency(discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                      <span>Total</span>
                      <span className="text-primary" data-testid="total-ttc">{formatCurrency(totalHT)}</span>
                    </div>
                    {acompteAmount > 0 && (
                      <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                        <span className="text-muted-foreground">Acompte ({acomptePercent}%)</span>
                        <span className="text-primary">{formatCurrency(acompteAmount)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Sous-total HT</span>
                      <span>{formatCurrency(subtotalHT)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-destructive">
                        <span>Remise</span>
                        <span>-{formatCurrency(discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total HT</span>
                      <span>{formatCurrency(totalHT)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">TVA</span>
                      <span>{formatCurrency(totalTVA)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                      <span>Total TTC</span>
                      <span className="text-primary" data-testid="total-ttc">{formatCurrency(totalTTC)}</span>
                    </div>
                    {acompteAmount > 0 && (
                      <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                        <span className="text-muted-foreground">Acompte ({acomptePercent}%)</span>
                        <span className="text-primary">{formatCurrency(acompteAmount)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              <Button
                onClick={handleSave}
                disabled={saving || !selectedClientId || lines.length === 0}
                className="w-full h-12 mt-4 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
                data-testid="save-invoice-btn"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InvoiceBuilderPage;
